import { useEffect, useState, useCallback } from 'react';
import api from '../api';

const COMPONENTES = [
  { value: '',          label: 'Todos' },
  { value: 'api',       label: 'API' },
  { value: 'agente',    label: 'Agente IA' },
  { value: 'asterisk',  label: 'Asterisk' },
  { value: 'liwa',      label: 'Liwa/SIP' },
  { value: 'livekit',   label: 'LiveKit' },
  { value: 'n8n',       label: 'n8n' },
  { value: 'sistema',   label: 'Sistema' },
];

const SEVERIDADES = [
  { value: '',            label: 'Todas' },
  { value: 'critico',     label: 'Crítico' },
  { value: 'advertencia', label: 'Advertencia' },
  { value: 'info',        label: 'Informativo' },
];

const ESTADOS = [
  { value: '',           label: 'Todos' },
  { value: 'nuevo',       label: 'Nuevo' },
  { value: 'en_revision', label: 'En revisión' },
  { value: 'resuelto',    label: 'Resuelto' },
];

const SEV_COLOR = {
  critico:     { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)',  text: 'var(--danger)' },
  advertencia: { bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)',   text: 'var(--warning)' },
  info:        { bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)',   text: 'var(--info)' },
};

const ESTADO_COLOR = {
  nuevo:        { bg: 'var(--danger-soft)', text: 'var(--danger)' },
  en_revision:  { bg: 'rgba(245,158,11,0.1)',  text: 'var(--warning)' },
  resuelto:     { bg: 'var(--success-soft)',  text: 'var(--success)' },
};

const COMPONENTE_LABEL = Object.fromEntries(COMPONENTES.map(c => [c.value, c.label]));

export default function AdminEventos() {
  const [eventos, setEventos]   = useState([]);
  const [resumen, setResumen]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [filtros, setFiltros]   = useState({ componente: '', severidad: '', estado: '' });
  const [expandido, setExpandido] = useState(null);

  const cargar = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filtros.componente) params.componente = filtros.componente;
    if (filtros.severidad)  params.severidad  = filtros.severidad;
    if (filtros.estado)     params.estado     = filtros.estado;
    api.get('/admin/eventos-sistema', { params })
      .then(r => setEventos(r.data.eventos || []))
      .catch(() => setEventos([]))
      .finally(() => setLoading(false));
  }, [filtros]);

  const cargarResumen = useCallback(() => {
    api.get('/admin/eventos-sistema/resumen').then(r => setResumen(r.data)).catch(() => {});
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const cambiarEstado = (id, nuevoEstado) => {
    api.patch(`/admin/eventos-sistema/${id}`, { estado: nuevoEstado })
      .then(() => { cargar(); cargarResumen(); })
      .catch(() => {});
  };

  const fmtFecha = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const selectStyle = {
    background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 8,
    color: 'var(--text)', padding: '7px 10px', fontSize: 12, outline: 'none', cursor: 'pointer',
  };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Eventos del Sistema</h1>
      <p style={{ color: 'var(--text-3)', marginBottom: 24, fontSize: 13 }}>
        Diagnóstico centralizado · Asterisk, LiveKit, Agente, API, Liwa/SIP y n8n
      </p>

      {/* Resumen por componente */}
      {resumen && resumen.total_nuevos > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
          {Object.entries(resumen.resumen).map(([comp, counts]) => {
            const total = counts.critico + counts.advertencia + counts.info;
            if (total === 0) return null;
            return (
              <div key={comp} style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  {COMPONENTE_LABEL[comp] || comp}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  {counts.critico > 0 && <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: 16 }}>{counts.critico}</span>}
                  {counts.advertencia > 0 && <span style={{ color: 'var(--warning)', fontWeight: 700, fontSize: 16 }}>{counts.advertencia}</span>}
                  {counts.info > 0 && <span style={{ color: 'var(--info)', fontWeight: 700, fontSize: 16 }}>{counts.info}</span>}
                  <span style={{ fontSize: 10, color: 'var(--text-3)' }}>sin revisar</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <select style={selectStyle} value={filtros.componente} onChange={e => setFiltros(f => ({ ...f, componente: e.target.value }))}>
          {COMPONENTES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select style={selectStyle} value={filtros.severidad} onChange={e => setFiltros(f => ({ ...f, severidad: e.target.value }))}>
          {SEVERIDADES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select style={selectStyle} value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
          {ESTADOS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={() => { cargar(); cargarResumen(); }} style={{
          ...selectStyle, background: 'var(--accent-soft)', border: '1px solid var(--accent-soft-2)', color: 'var(--accent)', fontWeight: 600,
        }}>Refrescar</button>
      </div>

      {/* Lista de eventos */}
      {loading ? (
        <div style={{ color: 'var(--text-3)', fontSize: 13, padding: 20 }}>Cargando eventos...</div>
      ) : eventos.length === 0 ? (
        <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 12, padding: 32, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
          No hay eventos con estos filtros. Eso es buena señal.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {eventos.map(ev => {
            const sev = SEV_COLOR[ev.severidad] || SEV_COLOR.info;
            const est = ESTADO_COLOR[ev.estado] || ESTADO_COLOR.nuevo;
            const isOpen = expandido === ev.id;
            return (
              <div key={ev.id} style={{
                background: 'var(--bg)', border: `1px solid ${isOpen ? sev.border : 'var(--surface)'}`,
                borderRadius: 10, padding: 14, cursor: 'pointer', transition: 'border-color 0.15s',
              }} onClick={() => setExpandido(isOpen ? null : ev.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                    background: sev.bg, color: sev.text, border: `1px solid ${sev.border}`, textTransform: 'uppercase',
                  }}>{ev.severidad}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                    background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent-soft-2)',
                  }}>{COMPONENTE_LABEL[ev.componente] || ev.componente}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600 }}>{ev.tipo_error}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>{fmtFecha(ev.date_created)}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: est.bg, color: est.text,
                  }}>{ESTADOS.find(e => e.value === ev.estado)?.label || ev.estado}</span>
                </div>

                {isOpen && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--surface)' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Mensaje original</div>
                    <div style={{
                      fontSize: 12, color: 'var(--text-2)', fontFamily: 'monospace', background: 'var(--bg)',
                      padding: 10, borderRadius: 6, marginBottom: 12, wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                    }}>{ev.mensaje}</div>

                    {ev.causa_probable && (
                      <>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Causa probable</div>
                        <div style={{ fontSize: 12, color: 'var(--success)', marginBottom: 12 }}>{ev.causa_probable}</div>
                      </>
                    )}

                    <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                      {ev.estado !== 'en_revision' && (
                        <button onClick={() => cambiarEstado(ev.id, 'en_revision')} style={{
                          ...selectStyle, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: 'var(--warning)',
                        }}>Marcar en revisión</button>
                      )}
                      {ev.estado !== 'resuelto' && (
                        <button onClick={() => cambiarEstado(ev.id, 'resuelto')} style={{
                          ...selectStyle, background: 'var(--success-soft)', border: '1px solid rgba(52,211,153,0.25)', color: 'var(--success)',
                        }}>Marcar resuelto</button>
                      )}
                      {ev.estado !== 'nuevo' && (
                        <button onClick={() => cambiarEstado(ev.id, 'nuevo')} style={{
                          ...selectStyle, background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', color: 'var(--text-2)',
                        }}>Reabrir</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
